import boasVindasImg from '@/assets/help/boas-vindas.png';
import laudoEditorImg from '@/assets/help/laudo-editor.png';
import painelImg from '@/assets/help/painel.png';
import perfilImg from '@/assets/help/perfil.png';
import type { HelpArticle } from '../types';

export const GETTING_STARTED_ARTICLES: HelpArticle[] = [
    {
        slug: 'visao-geral-da-plataforma',
        topicId: 'getting-started',
        title: 'Conheça o LaudoPsi por dentro',
        description:
            'Um passeio rápido pelo painel e pelo menu lateral para você saber onde encontrar cada coisa.',
        updatedAt: '2026-07-03',
        keywords: ['painel', 'menu', 'dashboard', 'navegação', 'começar', 'visão geral'],
        blocks: [
            {
                type: 'paragraph',
                text: 'O LaudoPsi foi organizado para acompanhar a sua rotina clínica. Tudo começa no **Painel**, que reúne seus laudos, e o menu lateral leva você para as outras áreas da plataforma.',
            },
            {
                type: 'image',
                src: painelImg,
                alt: 'Painel do LaudoPsi com a lista de laudos e o menu lateral',
            },
            { type: 'subheading', id: 'painel', text: 'O que você encontra no Painel' },
            {
                type: 'paragraph',
                text: 'O Painel é a sua página inicial. Nele você vê o total de laudos, quais estão em andamento e quais já foram concluídos. Também é dele que sai o botão **Novo Laudo**, o caminho mais rápido para começar um documento.',
            },
            { type: 'subheading', id: 'menu', text: 'O menu lateral, área por área' },
            {
                type: 'list',
                items: [
                    '**Painel**: sua lista de laudos, com busca e filtros por status.',
                    '**Pacientes**: cadastro e histórico de cada paciente.',
                    '**Formulários**: questionários que você envia para pacientes responderem, como a pré-anamnese.',
                    '**Instrumentos**: a biblioteca de escalas e testes que podem ser enviados ao paciente.',
                    '**Calculadoras**: conversor de escores e calculadora de escore Z.',
                    '**Perfil**: seus dados profissionais, como nome e CRP.',
                    '**Ajuda** e **Assinatura**: esta central de guias e a gestão do seu plano.',
                ],
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'Na primeira visita, um tour guiado apresenta as principais áreas. Se preferir explorar sozinho, é só clicar em Pular. Você também pode contar com o checklist no canto da tela para chegar ao seu primeiro laudo concluído.',
            },
            { type: 'subheading', id: 'proximo-passo', text: 'Por onde começar' },
            {
                type: 'paragraph',
                text: 'Nossa sugestão de primeiro fluxo é simples: complete seu perfil, cadastre um paciente e crie seu primeiro laudo. Os guias desta seção mostram cada um desses passos em detalhes.',
            },
        ],
    },
    {
        slug: 'primeiros-dias-e-teste-gratis',
        topicId: 'getting-started',
        title: 'Seus primeiros dias no teste grátis',
        description:
            'O que aproveitar logo no começo: a tela de boas-vindas, o período de teste de 14 dias e o checklist que guia você até o primeiro laudo.',
        updatedAt: '2026-07-03',
        keywords: [
            'teste grátis',
            'trial',
            'período de teste',
            'primeiros dias',
            'boas-vindas',
            'checklist',
            'onboarding',
            'assinar',
        ],
        blocks: [
            {
                type: 'paragraph',
                text: 'Boas-vindas ao LaudoPsi. No seu primeiro acesso você cai na tela de boas-vindas e a sua conta já está no período de teste. Este guia mostra o que fazer nesses primeiros dias para aproveitar bem.',
            },
            {
                type: 'image',
                src: boasVindasImg,
                alt: 'Tela de boas-vindas do LaudoPsi após o primeiro acesso',
            },
            { type: 'subheading', id: 'teste-gratis', text: 'Como funciona o teste grátis' },
            {
                type: 'paragraph',
                text: 'Sua conta começa com **14 dias de teste**. Nesse período você pode explorar a plataforma, cadastrar paciente, criar um laudo e experimentar os recursos de IA dentro dos limites do teste.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'O banner no topo da tela mostra quantos dias de teste ainda restam. Quando quiser liberar mais volume de trabalho, é só escolher um plano em **Assinatura**.',
            },
            { type: 'subheading', id: 'checklist', text: 'Seu checklist de primeiros passos' },
            {
                type: 'paragraph',
                text: 'No canto da tela, o painel **Você está indo bem** acompanha o seu progresso. Ele lista as etapas até o seu primeiro laudo concluído: completar o perfil, adicionar um paciente, criar um laudo e concluí-lo. Cada etapa que você finaliza marca um passo a menos. Use o checklist como um roteiro dos primeiros dias.',
            },
            { type: 'subheading', id: 'por-onde-comecar', text: 'Por onde começar' },
            {
                type: 'paragraph',
                text: 'Na tela de boas-vindas você pode clicar em **Explorar a plataforma** para conhecer o painel com calma ou em **Criar meu primeiro laudo** para ir direto ao editor. Os dois caminhos são válidos, siga o que combina mais com você.',
            },
        ],
    },
    {
        slug: 'complete-seu-perfil',
        topicId: 'getting-started',
        title: 'Complete seu perfil profissional',
        description:
            'Informe seu nome e CRP para que os laudos saiam com a identificação correta e sem bloqueios na hora de concluir.',
        updatedAt: '2026-07-03',
        keywords: ['perfil', 'crp', 'nome', 'assinatura', 'identificação', 'dados profissionais'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Os dados do seu perfil aparecem no laudo final, no cabeçalho e no bloco de assinatura. Por isso vale a pena preenchê-los logo no início.',
            },
            { type: 'image', src: perfilImg, alt: 'Página de perfil do psicólogo no LaudoPsi' },
            { type: 'subheading', id: 'como-preencher', text: 'Como preencher' },
            {
                type: 'steps',
                items: [
                    'No menu lateral, clique em **Perfil**.',
                    'Informe seu **nome completo** como deve aparecer nos documentos.',
                    'Preencha o seu **CRP**. Ele acompanha a sua assinatura no laudo.',
                    'Salve as alterações.',
                ],
            },
            {
                type: 'callout',
                tone: 'warning',
                text: 'Sem nome e CRP preenchidos, a conclusão do laudo pode ficar bloqueada. Preencher o perfil agora evita esse contratempo depois.',
            },
            { type: 'subheading', id: 'onde-aparece', text: 'Onde esses dados aparecem' },
            {
                type: 'paragraph',
                text: 'O nome e o CRP alimentam o campo **Autor(a)** do documento e a linha de assinatura no final do laudo. Você também pode personalizar o cabeçalho com logo e outras informações no editor, pelo botão **Personalizar**.',
            },
        ],
    },
    {
        slug: 'seu-primeiro-laudo',
        topicId: 'getting-started',
        title: 'Crie seu primeiro laudo do início ao fim',
        description:
            'O caminho completo em poucos minutos: paciente, seções, resultados e exportação.',
        updatedAt: '2026-07-03',
        keywords: ['primeiro laudo', 'novo laudo', 'criar laudo', 'fluxo', 'passo a passo'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Este guia mostra o fluxo completo de um laudo, do clique em **Novo Laudo** até a exportação. Cada etapa tem um guia próprio na seção **Laudos**, então aqui vamos direto ao ponto.',
            },
            { type: 'subheading', id: 'comecando', text: 'Começando' },
            {
                type: 'steps',
                items: [
                    'Clique em **Criar → Novo Laudo** no menu lateral (o botão **Novo Laudo** também está no Painel).',
                    'Escolha um ponto de partida: **Criar do zero** ou um template do catálogo.',
                    'Na etapa **Informações do Paciente**, selecione ou crie o paciente e informe as datas de início e término da avaliação. A idade no exame é calculada automaticamente, em anos, meses e dias.',
                ],
            },
            {
                type: 'image',
                src: laudoEditorImg,
                alt: 'Editor de laudos com o paciente selecionado e a idade calculada automaticamente',
            },
            { type: 'subheading', id: 'escrevendo', text: 'Escrevendo as seções' },
            {
                type: 'paragraph',
                text: 'Siga as etapas do menu à esquerda, de **Descrição da Demanda** até **Recomendações**. Em cada etapa, o painel à direita oferece atalhos como modelos prontos e geração de rascunho com IA. O documento no centro mostra o resultado em tempo real, com a mesma cara do arquivo final.',
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'Você não precisa seguir a ordem. Dá para pular para Análise, voltar para a demanda e ajustar tudo no seu ritmo. Use o botão Salvar para guardar o progresso.',
            },
            { type: 'subheading', id: 'concluindo', text: 'Concluindo e exportando' },
            {
                type: 'steps',
                items: [
                    'Revise o documento no preview central. Se quiser, ajuste cores, fontes e cabeçalho em **Personalizar**.',
                    'Clique em **Concluir laudo** no canto superior direito.',
                    'O arquivo é exportado em **DOCX**, pronto para abrir no Word com a mesma formatação do preview.',
                ],
            },
            {
                type: 'callout',
                tone: 'warning',
                text: 'Depois de concluído e exportado, o laudo fica somente leitura. Confira o conteúdo com calma antes de concluir.',
            },
        ],
    },
];
